import styled from 'helpers/styles';
import {css} from 'styled-components';
import PropTypes from 'prop-types';
import ComponentStyles from 'styles/mixins/Component.styles';
import utils from 'helpers/utils';
import HtmlStyles from 'styles/mixins/Html.styles';
import {RichTextEditor} from 'mui-tiptap';

const StyledEditorInput = styled(RichTextEditor)
  .attrs((props) => {
    let attr = props;

    const vars = {
      '--editor-input-min-height': utils.isDefined(props.$rows ?? props.$minRows) ?
        props.theme.layout((((props.$rows ?? props.$minRows) * 24) + (2 * 8))) : null,
      '--editor-input-max-height': utils.isDefined(props.$rows ?? props.$maxRows) ?
        props.theme.layout((((props.$rows ?? props.$maxRows) * 24) + (2 * 8))) : null,
      '--editor-input-dense-min-height': utils.isDefined(props.$rows ?? props.$minRows) ?
        props.theme.layout((((props.$rows ?? props.$minRows) * 22.5) + (2 * 6.5))) : null,
      '--editor-input-dense-max-height': utils.isDefined(props.$rows ?? props.$maxRows) ?
        props.theme.layout((((props.$rows ?? props.$maxRows) * 22.5) + (2 * 6.5))) : null
    };

    // RichTextEditor swallows a top-level `style` prop into useEditor's options
    // and never forwards it to the DOM. Route the vars through RichTextFieldProps
    // so they land on FieldContainer — the same element that carries the class.
    attr = utils.styleProps(attr, vars);
    return {
      $css: attr.$css,
      RichTextFieldProps: {
        ...props.RichTextFieldProps,
        style: {
          ...props.RichTextFieldProps?.style,
          ...attr.style
        }
      }
    };
  })`
  &.EditorInput {
    display: flex;
    flex-direction: column;
    padding: 0 !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    border: none !important;
    scroll-padding: ${props => props.theme.layout(8 + 4)} ${props => props.theme.layout(14 + 4)} !important;

    .ProseMirror {
      font-size: inherit !important;
      font-weight: inherit !important;
      font-family: inherit !important;
      line-height: inherit !important;

      .mention-component {
        display: inline;
        max-width: fit-content;
      }
      
      img:not(.ProseMirror-separator) {
        &.ProseMirror-selectednode {
          outline: 1px solid ${props => props.theme.property('palette.primary.main')};
          
          ~ div {
            bottom: ${props => props.theme.layout(-1)};
            right: ${props => props.theme.layout(-1)};
          }
        }
      }
    }
    
    .MuiTiptap-RichTextContent-root {
      flex: 1 0 100%;
      width: 100%;
      min-height: fit-content !important;
      padding: 0 !important;
      
      .ProseMirror {
        min-height: 100% !important;
        min-width: 100% !important;
      }
    }
      
    .MuiTiptap-FieldContainer-notchedOutline {
      display: none;
    }

    ${props => utils.isDefined(props.$rows ?? props.$minRows) ? css`
      min-height: ${props => props.$css.$var('--editor-input-min-height', false)};
    ` : null}

    ${props => utils.isDefined(props.$rows ?? props.$maxRows) ? css`
      max-height: ${props => props.$css.$var('--editor-input-max-height', false)};
    ` : null}
  }
  
  &.EditorInput-showMenu {
    scroll-padding-bottom: ${props => props.theme.layout(38)} !important;

    .MuiTiptap-RichTextField-content {
      flex: 1 0 calc(100% - ${props => props.theme.layout(38)});
      margin-bottom: ${props => props.theme.layout(38)} !important;

      .ProseMirror {
        scroll-margin-bottom: ${props => props.theme.layout(38)};
        * {
          scroll-margin-bottom: inherit;
        }
      }
    }
    
    ${props => utils.isDefined(props.$rows ?? props.$minRows) ? css`
      min-height: max(${props => props.theme.layout((3 * 24 + 38) + (2 * 8))}, ${props => props.$css.$var('--editor-input-min-height', false)});
    ` : css`
      min-height: ${props => props.theme.layout((3 * 24 + 38) + (2 * 8))};
    `}
  }
  
  &.EditorInput-size-smaller {
    scroll-padding: ${props => props.theme.layout(6.5 + 4)} ${props => props.theme.layout(12 + 4)} !important;
    
    ${props => utils.isDefined(props.$rows ?? props.$minRows) ? css`
      min-height: ${props => props.$css.$var('--editor-input-dense-min-height', false)};
    ` : null}

    ${props => utils.isDefined(props.$rows ?? props.$maxRows) ? css`
      max-height: ${props => props.$css.$var('--editor-input-dense-max-height', false)};
    ` : null}

    ~ .EditorMenu {
      padding: ${props => props.theme.spacing(2.5/8)} ${props => props.theme.spacing(3/8)};
    }
    
    &.EditorInput-showMenu {
      ${props => utils.isDefined(props.$rows ?? props.$minRows) ? css`
        min-height: max(${props => props.theme.layout((3 * 22.5 + 38) + (2 * 6.5))}, ${props => props.$css.$var('--editor-input-dense-min-height', false)});
      ` : css`
        min-height: ${props => props.theme.layout((3 * 22.5 + 38) + (2 * 6.5))};
      `}
    }
  }

  ${HtmlStyles}
  ${ComponentStyles}
`;

StyledEditorInput.propTypes = {
  theme: PropTypes.object
};

export default StyledEditorInput;
